/**
 * Join Status Notification Component
 * Displays a notification when a user's join request is accepted or rejected
 * Shows admin info and community preview card
 */

import { EllipsisVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface JoinStatusNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface JoinStatusNotificationData {
  accepterId?: string | null
  accepterName?: string
  accepterUsername?: string
  rejecterId?: string | null
  rejecterName?: string
  rejecterUsername?: string
  communityId?: string
  communityName?: string
  communityHandle?: string
  communityCoverImageUrl?: string
  reason?: string
  city?: string
  state?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function JoinStatusNotification({
  notification,
  onRead,
  onUnread,
}: JoinStatusNotificationProps) {
  const navigate = useNavigate()

  const data = notification.data as JoinStatusNotificationData
  const isAccepted = notification.action === "join_request_accepted"
  const admin = notification.triggeredByUser

  if (!admin || !data) {
    return null
  }

  const adminUser = admin
  const adminUsername = isAccepted ? data.accepterUsername : data.rejecterUsername
  const communityName = data.communityName || "a community"
  const city = data.city
  const state = data.state
  const location = city && state ? `${city}, ${state}` : null
  const communityCoverImageUrl = data.communityCoverImageUrl
  const communityId = data.communityId
  const isRead = notification.isRead

  const displayImageUrl = getUserDisplayImage(adminUser)
  const displayName = adminUser.name || "Someone"

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/profile/${adminUser.id}`)
    if (!isRead) {
      onRead?.()
    }
  }

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/communities/${communityId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Admin avatar */}
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleAvatarClick}
          >
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {adminUsername && <span className="text-stone text-[14px]">@{adminUsername}</span>}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              {isAccepted ? (
                <>
                  <span className="font-semibold">{displayName}</span> accepted your request to join{" "}
                  <span className="font-medium cursor-pointer" onClick={handleCommunityClick}>
                    {communityName}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold">{displayName}</span> declined your request to join{" "}
                  <span className="font-medium cursor-pointer" onClick={handleCommunityClick}>
                    {communityName}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Community Card */}
      <div
        className="relative mt-2 md:h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden cursor-pointer border border-card"
        onClick={handleCommunityClick}
      >
        {communityCoverImageUrl ? (
          <img
            src={communityCoverImageUrl}
            alt={communityName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {communityName}
            </span>
            {location && (
              <span className="text-white/80 text-[13px] leading-[20px]">{location}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
